/**
 * AI Search — storefront flydown integration
 *
 * HOW TO INSTALL
 * ──────────────
 * 1. Upload this file: Online Store → Themes → Edit code → Assets → ai-search.js
 * 2. Add one line in layout/theme.liquid just before </body>:
 *    {{ 'ai-search.js' | asset_url | script_tag }}
 * 3. To kill the flash-of-native-results (FOUC) on the /search page, paste this
 *    render-blocking snippet inside <head> in layout/theme.liquid (BEFORE any
 *    content). It hides the native grid before it can paint; this asset then
 *    fills in AI results, or reveals native again if AI returns nothing:
 *
 *    <script>
 *    (function(){try{
 *      if(!/^\/search\/?$/.test(location.pathname))return;
 *      if((new URLSearchParams(location.search).get('q')||'').trim().length<2)return;
 *      var s=document.createElement('style');s.id='ai-search-early-hide';
 *      s.textContent='.productgrid--wrapper .productgrid--items,.productgrid--wrapper .productgrid--no-results{display:none!important}#americord-ai-page-results .productgrid--items{display:grid!important}';
 *      document.head.appendChild(s);
 *    }catch(e){}})();
 *    </script>
 */

(function () {
  'use strict';

  /* ── config ───────────────────────────────────────────────────────────────── */
  var DEBOUNCE_MS = 180;
  var MIN_QUERY_LEN = 2;
  var MAX_RESULTS = 6;
  var PAGE_MAX_RESULTS = 24;
  var AI_SECTION_ID = 'americord-ai-results';
  var AI_PAGE_ID = 'americord-ai-page-results';
  var AI_HOT_SECTION_ID = 'americord-ai-hot-searches';
  var SEARCH_SPLIT_ID = 'americord-ai-search-split';
  var PROXY_PATH = '/apps/americord/search';
  var HOT_SEARCH_PATH = '/apps/americord/hot-searches';
  var HOT_SEARCH_LIMIT = 8;
  var CATEGORY_LIMIT = 6; // how many "Popular categories" to show in the empty-state flydown
  // "Popular categories" shown in the search flydown. These mirror the storefront's
  // "Categories" mega-menu (qikify Smart Menu) — each label links to its Shopify
  // collection, so clicking one lands on that category's product listing.
  // Handles are taken from the live menu config; edit this list to curate what shows.
  var CATEGORIES = [
    { label: 'Power Cords', handle: 'power-cords' },
    { label: 'Extension Cords', handle: 'extension-cords' },
    { label: 'Bulk Wire', handle: 'bulk-wire' },
    { label: 'GFCI & Plugs', handle: 'gfci' },
    { label: 'Power Strips', handle: 'power-strips' },
    { label: 'Clearance', handle: 'clearance' },
  ];
  // Shopify App Proxy injects ?shop= automatically — no need to send it from the browser

  /* ── prevent the native search grid from flashing before AI renders ─────────── */
  // The theme renders its product grid server-side, so it paints before this script
  // (loaded at end of <body>) can swap in AI results. We tag the grid wrapper as
  // "pending" the instant the script runs — hiding native results until AI decides —
  // and reveal native again only if AI returns nothing.
  var PENDING_CLASS = 'ai-suggest-pending';
  var EARLY_HIDE_ID = 'ai-search-early-hide'; // shared with the render-blocking <head> snippet
  var FAILSAFE_REVEAL_MS = 6000;

  function isSearchResultsPage() {
    if (!/^\/search\/?$/.test(window.location.pathname)) return false;
    try {
      var q = (new URLSearchParams(window.location.search).get('q') || '').trim();
      return q.length >= MIN_QUERY_LEN;
    } catch (e) { return false; }
  }

  // Reveal the native grid by REMOVING the hide <style> (its rule is !important, so
  // toggling inline display alone can't override it). Native list/​no-results then
  // fall back to whatever inline visibility runSearchPageAI set.
  function clearSearchPagePending() {
    var s = document.getElementById(EARLY_HIDE_ID);
    if (s && s.parentNode) s.parentNode.removeChild(s);
    var w = document.querySelector('.' + PENDING_CLASS);
    if (w) w.classList.remove(PENDING_CLASS);
  }

  function injectEarlyHideStyles() {
    if (document.getElementById(EARLY_HIDE_ID)) return; // already injected (e.g. by the <head> snippet)
    var s = document.createElement('style');
    s.id = EARLY_HIDE_ID;
    // Hide the native grid + no-results outright; the id rule keeps the AI grid shown.
    // Only injected on /search pages, so collection/other grids are never affected.
    s.textContent =
      '.productgrid--wrapper .productgrid--items,' +
      '.productgrid--wrapper .productgrid--no-results{display:none!important}' +
      '#' + AI_PAGE_ID + ' .productgrid--items{display:grid!important}';
    (document.head || document.documentElement).appendChild(s);
  }

  // Best-effort hide from the asset itself. NOTE: this script loads near </body>, so
  // the grid may already be painted — the render-blocking <head> snippet (see install
  // notes) is what actually prevents the flash; this is the body-side fallback.
  (function earlyHideNativeGrid() {
    if (!isSearchResultsPage()) return;
    injectEarlyHideStyles();
    // Fail-safe: if AI never resolves (app blocked/down), reveal native after a bit.
    setTimeout(clearSearchPagePending, FAILSAFE_REVEAL_MS);
  })();

  /* ── tiny helpers ─────────────────────────────────────────────────────────── */
  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function debounce(fn, ms) {
    var t;
    return function () { var a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(null, a); }, ms); };
  }

  /* ── styles injected once ─────────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('ai-search-styles')) return;
    var s = document.createElement('style');
    s.id = 'ai-search-styles';
    s.textContent =
      '#' + AI_SECTION_ID + '{padding-bottom:8px}' +
      '#' + AI_SECTION_ID + ' .ai-section-title{display:flex;align-items:center;gap:6px}' +
      '#' + AI_SECTION_ID + ' .ai-section-title::before{content:"✦";font-size:11px;opacity:.55}' +
      '.ai-score-badge{display:inline-block;font-size:10px;font-weight:700;padding:2px 5px;' +
      'border-radius:99px;background:#eff6ff;color:#2563eb;margin-left:5px;vertical-align:middle}' +
      '.ai-score-badge--high{background:#ecfdf5;color:#059669}' +
      '.ai-powered-by{font-size:10px;color:#9ca3af;text-align:right;padding:4px 0 0;letter-spacing:.03em}' +
      /* skeleton shimmer */
      '@keyframes ai-shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}' +
      '.ai-skeleton-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid rgba(0,0,0,.06)}' +
      '.ai-skeleton-row:last-child{border-bottom:none}' +
      '.ai-skeleton-img{width:52px;height:52px;border-radius:6px;flex-shrink:0;' +
      'background:linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);' +
      'background-size:800px 100%;animation:ai-shimmer 1.4s infinite linear}' +
      '.ai-skeleton-lines{flex:1}' +
      '.ai-skeleton-line{height:12px;border-radius:4px;margin-bottom:6px;' +
      'background:linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%);' +
      'background-size:800px 100%;animation:ai-shimmer 1.4s infinite linear}' +
      '.ai-skeleton-line.short{width:50%}' +
      '.ai-skeleton-line.shorter{width:30%;margin-bottom:0}' +
      /* Search page: AI block above native Shopify grid (same column; native hits follow AI) */
      '.americord-ai-page-root{margin:0 0 28px;clear:both;width:100%}' +
      '.americord-ai-page__title{font-size:1.5rem;font-weight:700;margin:0 0 16px;color:#111}' +
      '.americord-ai-page .productgrid--items{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin:0;padding:0;list-style:none}' +
      '.americord-ai-page .productgrid--item{width:100% !important;margin:0 !important;max-width:none !important;float:none !important;clear:none !important}' +
      '@media (max-width:900px){.americord-ai-page .productgrid--items{grid-template-columns:repeat(3,1fr)}}' +
      '@media (max-width:550px){.americord-ai-page .productgrid--items{grid-template-columns:repeat(2,1fr);gap:12px}}' +
      '.americord-ai-card{border:1px solid #e5e7eb;background:#fff;height:100%;display:flex;flex-direction:column}' +
      '.americord-ai-card__img-wrap{aspect-ratio:1/1;background:#fafafa;border-bottom:1px solid #eee;display:flex;align-items:center;justify-content:center}' +
      '.americord-ai-card__img{max-width:100%;max-height:100%;object-fit:contain}' +
      '.americord-ai-card__body{padding:14px 16px}' +
      '.americord-ai-card__price{font-weight:700;font-size:16px;color:#0d9488;margin-bottom:8px}' +
      '.americord-ai-card__title{font-size:14px;line-height:1.45;color:#111;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;min-height:60px}' +
      '.americord-ai-card__sku{font-size:13px;color:#555;margin-top:8px}' +
      '.americord-ai-card__stock{font-size:12px;margin-top:6px;display:block}' +
      '.americord-ai-card__stock--in{color:#16a34a}' +
      '.americord-ai-card__stock--out{color:#dc2626}' +
      '.americord-ai-card__score{font-size:12px;color:#64748b;margin-top:8px;display:block}' +
      /* suggestions flydown panel (empty-state): AI Suggestions header + two columns + footer */
      '#' + AI_HOT_SECTION_ID + '{padding:14px 16px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 0 14px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-head__title{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700;color:#111827;letter-spacing:.01em}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-head__title::before{content:"✦";color:#dc2626;font-size:12px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-head__tag{font-size:10px;font-weight:600;letter-spacing:.06em;color:#9ca3af;text-transform:uppercase;white-space:nowrap}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-cols{display:flex;gap:32px;align-items:flex-start}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-col{flex:1;min-width:0}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-col__title{font-size:11px;font-weight:600;letter-spacing:.06em;color:#9ca3af;text-transform:uppercase;margin:0 0 8px;padding:0 4px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-hot-list,#' + AI_HOT_SECTION_ID + ' .ai-cat-list{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:2px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-hot-item,#' + AI_HOT_SECTION_ID + ' .ai-cat-item{margin:0}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-hot-link,#' + AI_HOT_SECTION_ID + ' .ai-cat-link{display:flex;align-items:center;gap:9px;padding:7px 8px;border-radius:6px;color:#111827;text-decoration:none;font-size:14px;line-height:1.3;transition:background .15s}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-hot-link:hover,#' + AI_HOT_SECTION_ID + ' .ai-cat-link:hover{background:#f3f4f6}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-hot-link::before{content:"";width:14px;height:14px;flex-shrink:0;background:no-repeat center/contain url("data:image/svg+xml,%3Csvg%20xmlns=\'http://www.w3.org/2000/svg\'%20viewBox=\'0%200%2024%2024\'%20fill=\'none\'%20stroke=\'%239ca3af\'%20stroke-width=\'2\'%20stroke-linecap=\'round\'%3E%3Ccircle%20cx=\'11\'%20cy=\'11\'%20r=\'7\'/%3E%3Cline%20x1=\'21\'%20y1=\'21\'%20x2=\'16.65\'%20y2=\'16.65\'/%3E%3C/svg%3E")}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-cat-link{justify-content:space-between}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-cat-link .ai-cat-label{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-cat-link::after{content:"→";font-size:13px;color:#9ca3af;flex-shrink:0;margin-left:8px}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-foot{margin:14px 0 0;padding:10px 4px 0;border-top:1px solid rgba(0,0,0,.07);font-size:12px;color:#6b7280;text-align:center}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-foot kbd{font-family:inherit;background:#f3f4f6;border:1px solid #e5e7eb;border-radius:4px;padding:1px 6px;font-size:11px;color:#374151}' +
      '#' + AI_HOT_SECTION_ID + ' .ai-suggest-foot strong{color:#374151;font-weight:600}' +
      '@media (max-width:600px){#' + AI_HOT_SECTION_ID + ' .ai-suggest-cols{flex-direction:column;gap:16px}}';
    document.head.appendChild(s);
  }

  /* ── skeleton placeholder while fetch is in-flight ───────────────────────── */
  function renderSkeleton() {
    var rows = '';
    for (var i = 0; i < 3; i++) {
      rows +=
        '<div class="ai-skeleton-row">' +
        '<div class="ai-skeleton-img"></div>' +
        '<div class="ai-skeleton-lines">' +
        '<div class="ai-skeleton-line"></div>' +
        '<div class="ai-skeleton-line short"></div>' +
        '<div class="ai-skeleton-line shorter"></div>' +
        '</div>' +
        '</div>';
    }
    return rows;
  }

  /* ── DOM: create AI section and place it ABOVE Shopify's results ──────────── */
  function getOrCreateSection(flydown) {
    var el = document.getElementById(AI_SECTION_ID);
    if (el) return el;

    el = document.createElement('div');
    el.id = AI_SECTION_ID;
    el.style.display = 'none';
    el.innerHTML =
      '<h3 class="search-flydown__group-title ai-section-title">AI Suggestions</h3>' +
      '<div id="' + AI_SECTION_ID + '-list"></div>' +
      '<div class="ai-powered-by">Powered by AI Search</div>';

    // Insert BEFORE Shopify's [data-live-search-results] so we appear at the top
    var shopifyResults = flydown.querySelector('[data-live-search-results]');
    if (shopifyResults) {
      flydown.insertBefore(el, shopifyResults);
    } else {
      flydown.appendChild(el);
    }
    return el;
  }

  /* ── toggle Shopify's own "No matching results" message ───────────────────── */
  function setShopifyNoResultsVisible(flydown, visible) {
    // The predictive-search template renders: <p class="search-flydown__no-results">
    var noRes = flydown.querySelector('.search-flydown__no-results');
    if (noRes) noRes.style.display = visible ? '' : 'none';
  }

  /* ── render product cards matching the existing theme structure ───────────── */
  function renderResults(results) {
    var items = results.slice(0, MAX_RESULTS).map(function (r) {
      var imgHtml = r.imageUrl
        ? '<figure class="search-flydown--product-image">' +
        '<img src="' + esc(r.imageUrl) + '" alt="' + esc(r.title) + '" ' +
        'width="70" height="70" loading="lazy" style="object-fit:cover">' +
        '</figure>'
        : '<figure class="search-flydown--product-image"></figure>';

      var priceHtml = r.price
        ? '<span class="search-flydown--product-price">' + esc(r.price) + '</span>'
        : '';

      var badgeClass = r.score >= 80 ? 'ai-score-badge ai-score-badge--high' : 'ai-score-badge';

      var stockHtml = r.inStock === false
        ? '<span style="font-size:11px;color:#ef4444;display:block">Out of stock</span>'
        : '';

      return (
        '<li><a class="search-flydown--product" href="' + esc(r.url) + '">' +
        imgHtml +
        '<div class="search-flydown--product-text">' +
        '<span class="search-flydown--product-title">' + esc(r.title) +
        '<span class="' + badgeClass + '">' + r.score + '%</span>' +
        '</span>' +
        priceHtml + stockHtml +
        '</div>' +
        '</a></li>'
      );
    }).join('');
    return '<ul class="search-flydown__group-list">' + items + '</ul>';
  }

  function renderPageResults(results) {
    var items = results.slice(0, PAGE_MAX_RESULTS).map(function (r) {
      var imgHtml = r.imageUrl
        ? '<img src="' + esc(r.imageUrl) + '" alt="' + esc(r.title) + '" class="americord-ai-card__img" loading="lazy">'
        : '';

      var priceHtml = r.price ? ('<div class="americord-ai-card__price"><span class="money">' + esc(r.price) + '</span></div>') : '';
      var titleHtml = '<div class="americord-ai-card__title">' + esc(r.title) + '</div>';

      var skuHtml = '';
      if (r.sku && String(r.sku).trim()) {
        var skuShow = String(r.sku).trim();
        if (skuShow.length > 96) skuShow = skuShow.slice(0, 93) + '…';
        skuHtml = '<div class="americord-ai-card__sku">SKU : <span class="variant-sku">' + esc(skuShow) + '</span></div>';
      }

      var stockHtml = '';
      if (r.inStock === true) {
        stockHtml = '<span class="americord-ai-card__stock americord-ai-card__stock--in">In stock</span>';
      } else if (r.inStock === false) {
        stockHtml = '<span class="americord-ai-card__stock americord-ai-card__stock--out">Out of stock</span>';
      }

      var scoreHtml = typeof r.score === 'number'
        ? '<span class="americord-ai-card__score">AI match: ' + r.score + '%</span>'
        : '';

      return (
        '<li class="productgrid--item imagestyle--cropped-small show-actions--mobile">' +
        '<div class="americord-ai-card">' +
        '<a href="' + esc(r.url) + '" style="text-decoration:none;color:inherit;">' +
        '<div class="americord-ai-card__img-wrap">' + imgHtml + '</div>' +
        '<div class="americord-ai-card__body">' +
        priceHtml +
        titleHtml +
        skuHtml +
        stockHtml +
        scoreHtml +
        '</div>' +
        '</a>' +
        '</div>' +
        '</li>'
      );
    }).join('');

    return (
      '<div class="americord-ai-page">' +
      '<h2 class="americord-ai-page__title">AI Suggestions</h2>' +
      '<ul class="productgrid--items products-per-row-4">' + items + '</ul>' +
      '</div>'
    );
  }

  /* ── show / hide AI section ───────────────────────────────────────────────── */
  function showSection(section, html, flydown) {
    var list = document.getElementById(AI_SECTION_ID + '-list');
    if (list) list.innerHTML = html;
    section.style.display = '';
    setShopifyNoResultsVisible(flydown, false);
  }

  function showSkeleton(section, flydown) {
    var list = document.getElementById(AI_SECTION_ID + '-list');
    if (list) list.innerHTML = renderSkeleton();
    section.style.display = '';
    setShopifyNoResultsVisible(flydown, false);
  }

  function hideSection(section, flydown) {
    var list = document.getElementById(AI_SECTION_ID + '-list');
    if (list) list.innerHTML = '';
    section.style.display = 'none';
    setShopifyNoResultsVisible(flydown, true);
  }

  /* ── hot searches (shown on focus when input is empty) ────────────────────── */
  var hotSearchesCache = null; // { rows: [...], ts: <ms> }
  var HOT_CACHE_TTL_MS = 5 * 60 * 1000;

  function getOrCreateHotSection(flydown) {
    var el = document.getElementById(AI_HOT_SECTION_ID);
    if (el) return el;

    el = document.createElement('div');
    el.id = AI_HOT_SECTION_ID;
    el.style.display = 'none';
    el.innerHTML =
      '<div class="ai-suggest-head">' +
        '<span class="ai-suggest-head__title">AI Suggestions</span>' +
        '<span class="ai-suggest-head__tag">Semantic match · Not just keywords</span>' +
      '</div>' +
      '<div class="ai-suggest-cols">' +
        '<div class="ai-suggest-col" id="' + AI_HOT_SECTION_ID + '-trend-col">' +
          '<div class="ai-suggest-col__title">Trending searches</div>' +
          '<ul class="ai-hot-list" id="' + AI_HOT_SECTION_ID + '-list"></ul>' +
        '</div>' +
        '<div class="ai-suggest-col" id="' + AI_HOT_SECTION_ID + '-cat-col">' +
          '<div class="ai-suggest-col__title">Popular categories</div>' +
          '<ul class="ai-cat-list" id="' + AI_HOT_SECTION_ID + '-cats"></ul>' +
        '</div>' +
      '</div>' +
      '<div class="ai-suggest-foot">Press <kbd>Enter</kbd> to see all results for <strong>any term</strong></div>';

    var aiResults = document.getElementById(AI_SECTION_ID);
    if (aiResults && aiResults.parentNode === flydown) {
      flydown.insertBefore(el, aiResults);
    } else {
      flydown.insertBefore(el, flydown.firstChild);
    }
    return el;
  }

  function renderHotSearches(rows) {
    return rows.map(function (r) {
      var q = String(r.query || '').trim();
      if (!q) return '';
      var href = '/search?q=' + encodeURIComponent(q);
      return '<li class="ai-hot-item"><a class="ai-hot-link" href="' + esc(href) + '" data-hot-query="' + esc(q) + '">' + esc(q) + '</a></li>';
    }).join('');
  }

  /* ── popular categories: curated list mirroring the storefront mega-menu ───── */
  /** Return up to `limit` category links ({ label, href }) from the CATEGORIES config. */
  function getCategoryLinks(limit) {
    return CATEGORIES.slice(0, limit).map(function (c) {
      var href = c.url || ('/collections/' + c.handle);
      return { label: c.label, href: href };
    });
  }

  function renderCategories(cats) {
    return cats.map(function (c) {
      return '<li class="ai-cat-item"><a class="ai-cat-link" href="' + esc(c.href) + '">' +
        '<span class="ai-cat-label">' + esc(c.label) + '</span></a></li>';
    }).join('');
  }

  function toggleCol(id, visible) {
    var col = document.getElementById(id);
    if (col) col.style.display = visible ? '' : 'none';
  }

  var _keepHotOpen = false;

  function ensureFlydownOpen(flydown) {
    if (!flydown) return;
    var liveSearch = flydown.closest('[data-live-search]');
    if (liveSearch) {
      liveSearch.classList.add('live-search--focused');
      liveSearch.classList.add('live-search--active');
    }
    flydown.setAttribute('data-animation-state', 'open');
    flydown.setAttribute('aria-hidden', 'false');
    flydown.removeAttribute('hidden');
    flydown.style.display = '';
  }

  /**
   * Populate the panel from trending rows (left) + curated categories (right),
   * then show it. Returns false (and shows nothing) when both columns are empty.
   */
  function showHotSection(section, rows, flydown) {
    var cats = getCategoryLinks(CATEGORY_LIMIT);
    if (!rows.length && !cats.length) { hideHotSection(section); return false; }

    var trendList = document.getElementById(AI_HOT_SECTION_ID + '-list');
    if (trendList) trendList.innerHTML = renderHotSearches(rows);
    toggleCol(AI_HOT_SECTION_ID + '-trend-col', rows.length > 0);

    var catList = document.getElementById(AI_HOT_SECTION_ID + '-cats');
    if (catList) catList.innerHTML = renderCategories(cats);
    toggleCol(AI_HOT_SECTION_ID + '-cat-col', cats.length > 0);

    section.style.display = '';
    _keepHotOpen = true;
    ensureFlydownOpen(flydown);
    return true;
  }

  function hideHotSection(section) {
    _keepHotOpen = false;
    if (!section) return;
    section.style.display = 'none';
  }

  function fetchHotSearches(section, flydown) {
    if (hotSearchesCache && Date.now() - hotSearchesCache.ts < HOT_CACHE_TTL_MS) {
      // Categories come from the curated CATEGORIES list, so show even with no trending rows.
      showHotSection(section, hotSearchesCache.rows, flydown);
      return;
    }

    var url = HOT_SEARCH_PATH + '?limit=' + encodeURIComponent(String(HOT_SEARCH_LIMIT));
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        var rows = (data && data.results) || [];
        hotSearchesCache = { rows: rows, ts: Date.now() };
        showHotSection(section, rows, flydown);
      })
      .catch(function (err) {
        console.warn('[AI Hot Search]', err.message);
        // Trending fetch failed — still show the curated categories column.
        showHotSection(section, [], flydown);
      });
  }

  /* ── fetch from App Proxy ─────────────────────────────────────────────────── */
  var currentQuery = '';
  var flydownAbort = null;

  function fetchAiResults(query, section, flydown) {
    if (!query || query.length < MIN_QUERY_LEN) { hideSection(section, flydown); return; }
    currentQuery = query;

    // Show skeleton immediately so the user sees feedback before the response arrives
    showSkeleton(section, flydown);

    if (flydownAbort) flydownAbort.abort();
    flydownAbort = new AbortController();

    // No &shop= needed — Shopify App Proxy appends it server-side when forwarding
    var url = PROXY_PATH + '?q=' + encodeURIComponent(query) + '&limit=' + encodeURIComponent(String(MAX_RESULTS));

    fetch(url, { headers: { Accept: 'application/json' }, signal: flydownAbort.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (query !== currentQuery) return; // discard stale response
        if (!data.success || !data.results || !data.results.length) {
          hideSection(section, flydown); return;
        }
        showSection(section, renderResults(data.results), flydown);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[AI Search]', err.message);
        hideSection(section, flydown);
      });
  }

  /** First theme product list inside `.productgrid--wrapper`, excluding our injected AI list. */
  function findShopifyProductListUl() {
    var wrap = document.querySelector('.productgrid--wrapper');
    if (!wrap) return null;
    var uls = wrap.querySelectorAll('ul.productgrid--items');
    for (var i = 0; i < uls.length; i++) {
      if (!uls[i].closest('#' + AI_PAGE_ID)) return uls[i];
    }
    return null;
  }

  function findSearchPageWrapper() {
    return document.querySelector('.productgrid--wrapper');
  }

  /** Theme's native search result list (the one we may hide when AI list is shown). */
  function findNativeThemeResultsList() {
    return findShopifyProductListUl();
  }

  function setNativeThemeResultsVisible(visible) {
    var nativeList = findNativeThemeResultsList();
    if (!nativeList) return;
    nativeList.style.display = visible ? '' : 'none';
  }

  /** Theme often renders `<h2 class="visually-hidden">Products</h2>` before the grid; keep AI above both when present. */
  function getNativeGridInsertAnchor(shopifyUl) {
    if (!shopifyUl) return null;
    var prev = shopifyUl.previousElementSibling;
    if (prev && prev.tagName === 'H2' && prev.classList.contains('visually-hidden')) return prev;
    return shopifyUl;
  }

  /** Undo older two-column layout: AI first, then theme `ul` (matches current above-grid placement). */
  function unwrapLegacySearchSplit() {
    var split = document.getElementById(SEARCH_SPLIT_ID);
    if (!split || !split.parentNode) return;
    var parent = split.parentNode;
    var main = split.querySelector('.americord-ai-search-split__main');
    var ul = main && main.querySelector('ul.productgrid--items');
    var section = document.getElementById(AI_PAGE_ID);
    if (ul) parent.insertBefore(ul, split);
    if (section && section.parentNode) parent.insertBefore(section, ul || split);
    parent.removeChild(split);
  }

  /** Place `#AI_PAGE_ID` above the theme product grid (AI first, native Shopify results below). */
  function ensureSearchPageAboveGrid(section) {
    unwrapLegacySearchSplit();

    var shopifyUl = findShopifyProductListUl();
    if (!shopifyUl || !shopifyUl.parentNode) {
      var wrapper = findSearchPageWrapper();
      if (!wrapper) return;
      var noResults = wrapper.querySelector('.productgrid--no-results');
      // Keep AI within wrapper even when native product list is absent.
      if (noResults) {
        if (section.parentNode === wrapper && section.nextSibling === noResults) return;
        wrapper.insertBefore(section, noResults);
        return;
      }
      if (section.parentNode !== wrapper) wrapper.appendChild(section);
      return;
    }

    var p = shopifyUl.parentNode;
    var anchor = getNativeGridInsertAnchor(shopifyUl);
    if (section.parentNode === p && section.nextSibling === anchor) return;
    p.insertBefore(section, anchor);
  }

  function fallbackInsertSearchSection(section) {
    var wrapper = findSearchPageWrapper();
    var noResults = wrapper ? wrapper.querySelector('.productgrid--no-results') : document.querySelector('.productgrid--no-results');
    var productList = findShopifyProductListUl();

    // Always keep the AI section inside the main productgrid wrapper.
    if (wrapper) {
      if (productList && productList.parentNode) {
        var anchor = getNativeGridInsertAnchor(productList);
        productList.parentNode.insertBefore(section, anchor);
        return;
      }
      if (noResults) {
        wrapper.insertBefore(section, noResults);
        return;
      }
      wrapper.appendChild(section);
      return;
    }
    if (productList && productList.parentNode) {
      var anchor = getNativeGridInsertAnchor(productList);
      productList.parentNode.insertBefore(section, anchor);
      return;
    }
    if (noResults && noResults.parentNode) noResults.parentNode.insertBefore(section, noResults);
  }

  function getOrCreateSearchPageSection() {
    var existing = document.getElementById(AI_PAGE_ID);
    if (existing) {
      ensureSearchPageAboveGrid(existing);
      if (!existing.parentNode) fallbackInsertSearchSection(existing);
      return existing;
    }

    var section = document.createElement('div');
    section.id = AI_PAGE_ID;
    section.className = 'americord-ai-page-root';
    section.style.display = 'none';

    var shopifyUl = findShopifyProductListUl();
    var wrapper = document.querySelector('.productgrid--wrapper');

    if (shopifyUl && wrapper && shopifyUl.closest('.productgrid--wrapper') === wrapper) {
      ensureSearchPageAboveGrid(section);
      if (!section.parentNode) fallbackInsertSearchSection(section);
      if (!section.parentNode) return null;
      return section;
    }

    fallbackInsertSearchSection(section);
    if (!section.parentNode) return null;
    return section;
  }

  function watchSearchPagePlacement(section) {
    if (!window.MutationObserver || !section) return;
    var wrapper = findSearchPageWrapper();
    if (!wrapper || wrapper.__aiPlacementWatcherAttached) return;
    wrapper.__aiPlacementWatcherAttached = true;

    var syncPlacement = debounce(function () {
      ensureSearchPageAboveGrid(section);
    }, 80);

    new MutationObserver(function () {
      syncPlacement();
    }).observe(wrapper, { childList: true, subtree: true });
  }

  function renderPageSkeleton(section) {
    var cards = '';
    for (var i = 0; i < 8; i++) {
      cards +=
        '<li class="productgrid--item imagestyle--cropped-small show-actions--mobile">' +
        '<div class="americord-ai-card">' +
        '<div class="americord-ai-card__img-wrap">' +
        '<div class="ai-skeleton-line" style="height:220px;margin-bottom:0;width:100%;"></div>' +
        '</div>' +
        '<div class="americord-ai-card__body">' +
        '<div class="ai-skeleton-line" style="width:40%;height:14px;margin:4px 0 10px;"></div>' +
        '<div class="ai-skeleton-line"></div>' +
        '<div class="ai-skeleton-line short"></div>' +
        '<div class="ai-skeleton-line" style="width:55%;height:10px;margin-top:8px;"></div>' +
        '</div>' +
        '</div>' +
        '</li>';
    }
    section.innerHTML =
      '<div class="americord-ai-page">' +
      '<h2 class="americord-ai-page__title">AI Suggestions</h2>' +
      '<ul class="productgrid--items products-per-row-4">' + cards + '</ul>' +
      '</div>';
    section.style.display = '';
  }

  function runSearchPageAI() {
    if (!/^\/search\/?$/.test(window.location.pathname)) return;

    var params = new URLSearchParams(window.location.search);
    var q = (params.get('q') || '').trim();
    if (q.length < MIN_QUERY_LEN) return;

    var section = getOrCreateSearchPageSection();
    if (!section) return;
    watchSearchPagePlacement(section);
    ensureSearchPageAboveGrid(section);

    var noResults = document.querySelector('.productgrid--no-results');
    // Prevent native "No matches found" flash while AI results are loading.
    if (noResults) noResults.style.display = 'none';

    renderPageSkeleton(section);
    setNativeThemeResultsVisible(false);

    var url = PROXY_PATH + '?q=' + encodeURIComponent(q) + '&limit=' + encodeURIComponent(String(PAGE_MAX_RESULTS));

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data.success || !data.results || !data.results.length) {
          // No AI results — reveal the native theme grid we were hiding.
          section.style.display = 'none';
          setNativeThemeResultsVisible(true);
          if (noResults) noResults.style.display = '';
          clearSearchPagePending();
          return;
        }

        // Hide native first (inline display:none persists), then drop the blanket
        // pending rule — native stays hidden, AI grid stays shown, no flash.
        section.innerHTML = renderPageResults(data.results);
        section.style.display = '';
        setNativeThemeResultsVisible(false);
        if (noResults) noResults.style.display = 'none';
        clearSearchPagePending();
      })
      .catch(function () {
        section.style.display = 'none';
        setNativeThemeResultsVisible(true);
        if (noResults) noResults.style.display = '';
        clearSearchPagePending();
      });
  }

  /* ── init ────────────────────────────────────────────────────────────────── */
  function init() {
    injectStyles();
    runSearchPageAI();

    // Try multiple selectors for resilience across theme versions
    var input = document.querySelector('[data-live-search-input]') ||
      document.querySelector('.live-search-form-field');
    var flydown = document.querySelector('[data-live-search-flydown]') ||
      document.querySelector('.search-flydown');

    if (!input || !flydown) {
      console.warn('[AI Search] search elements not found');
      return;
    }

    var section = getOrCreateSection(flydown);
    var hotSection = getOrCreateHotSection(flydown);

    var debouncedFetch = debounce(function () {
      fetchAiResults(input.value.trim(), section, flydown);
    }, DEBOUNCE_MS);

    input.addEventListener('input', function () {
      var q = input.value.trim();
      if (q.length >= MIN_QUERY_LEN) {
        // Show skeleton instantly; debounced call will replace it with real results
        showSkeleton(section, flydown);
        hideHotSection(hotSection);
        currentQuery = q;
      } else {
        hideSection(section, flydown);
        fetchHotSearches(hotSection, flydown);
      }
      debouncedFetch();
    });

    // Show hot searches when the user clicks/tabs into the empty search bar
    input.addEventListener('focus', function () {
      if (!input.value.trim()) fetchHotSearches(hotSection, flydown);
    });

    // Hide on blur so the flydown can close normally when user clicks away
    input.addEventListener('blur', function () {
      // Delay so clicks on the hot links register before we hide
      setTimeout(function () {
        if (document.activeElement !== input) hideHotSection(hotSection);
      }, 150);
    });

    // Clear AI section when input is emptied via the × button
    input.addEventListener('search', function () {
      if (!input.value) {
        hideSection(section, flydown);
        fetchHotSearches(hotSection, flydown);
      }
    });

    // Watch the flydown for close events
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          if (m.attributeName === 'style' || m.attributeName === 'class' || m.attributeName === 'hidden') {
            var closed = getComputedStyle(flydown).display === 'none' ||
              flydown.hidden ||
              flydown.getAttribute('aria-hidden') === 'true';
            if (closed) {
              if (_keepHotOpen && document.activeElement === input) {
                // Theme tried to close the flydown but we still want hot searches visible
                ensureFlydownOpen(flydown);
              } else {
                hideSection(section, flydown);
                hideHotSection(hotSection);
              }
            }
          }
        });
      }).observe(flydown, { attributes: true });
    }

    console.log('[AI Search] ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
